import type { ReactNode } from 'react'

import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import { alpha, type SxProps, type Theme } from '@mui/material/styles'

import { Text } from '@mxenabled/mxui'

export interface UpcomingBillDay {
  /** Day-of-week label, e.g. "Sun". */
  label: string
  /** Day-of-month number, e.g. "26". */
  date: string
  /** Whether this is the highlighted due day (blue label + filled date cell). */
  highlighted?: boolean
}

export interface UpcomingBillCardProps {
  /** The seven days of the week strip (exactly 7 items, one for each day). The due day should have `highlighted`. */
  days: [
    UpcomingBillDay,
    UpcomingBillDay,
    UpcomingBillDay,
    UpcomingBillDay,
    UpcomingBillDay,
    UpcomingBillDay,
    UpcomingBillDay,
  ]
  /** Merchant logo/icon (24x24), rendered left of the merchant name. */
  logo: ReactNode
  /** Merchant name, e.g. "Power Plus". */
  merchantName: string
  /** Bill amount, formatted (e.g. "$75.00"). */
  amount: string
  /** Custom aria-label for the highlighted due date cell (accessibility). Pass a localized string from copy/GlobalCopyStore; defaults to English "bill due" if not provided. */
  highlightedAriaLabel?: string
  sx?: SxProps<Theme>
}

/**
 * Upcoming Bill content: a seven-day week strip with the due day highlighted
 * (blue label + filled blue date cell) above a tinted bill row (merchant logo,
 * name, and amount).
 *
 * Figma: Upcoming Bill Content (Full variant). Scope is the Content section
 * only.
 */
const UpcomingBillCard = ({
  amount,
  days,
  highlightedAriaLabel,
  logo,
  merchantName,
  sx,
}: UpcomingBillCardProps) => {
  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        p: 12,
        ...sx,
      }}
    >
      <Stack sx={{ gap: 12 }}>
        {/* Week strip: labels row + date-cells row, aligned into 7 columns */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            rowGap: '2px',
          }}
        >
          {days.map((day) => (
            <Text
              bold={day.highlighted === true}
              color={day.highlighted === true ? 'primary.main' : 'text.secondary'}
              key={`label-${day.label}-${day.date}`}
              sx={{ textAlign: 'center' }}
              variant="caption"
            >
              {day.label}
            </Text>
          ))}
          {days.map((day) => (
            <Box
              aria-current={day.highlighted === true ? 'date' : undefined}
              aria-label={
                day.highlighted === true
                  ? (highlightedAriaLabel ?? `${day.label} ${day.date}, bill due`)
                  : undefined
              }
              key={`date-${day.label}-${day.date}`}
              sx={{
                alignItems: 'center',
                display: 'flex',
                height: 36,
                justifyContent: 'center',
                justifySelf: 'center',
                width: 36,
                ...(day.highlighted === true && {
                  bgcolor: 'primary.main',
                  borderRadius: '4px',
                }),
              }}
            >
              <Text
                color={day.highlighted === true ? 'primary.contrastText' : 'text.secondary'}
                variant="body1"
              >
                {day.date}
              </Text>
            </Box>
          ))}
        </Box>

        {/* Bill row */}
        <Stack
          sx={{
            alignItems: 'center',
            // Figma fill: primary.main at 15% opacity (a translucent overlay),
            // with a solid primary.main 1px border.
            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.15),
            border: '1px solid',
            borderColor: 'primary.main',
            borderRadius: '4px',
            flexDirection: 'row',
            gap: 12,
            justifyContent: 'space-between',
            px: 12,
            py: 10,
          }}
        >
          <Stack sx={{ alignItems: 'center', flexDirection: 'row', gap: 8, minWidth: 0 }}>
            <Box sx={{ flexShrink: 0, height: 24, width: 24 }}>{logo}</Box>
            <Text color="text.primary" truncate={true} variant="subtitle2">
              {merchantName}
            </Text>
          </Stack>
          <Text color="text.primary" sx={{ flexShrink: 0, fontWeight: 600 }} variant="subtitle2">
            {amount}
          </Text>
        </Stack>
      </Stack>
    </Box>
  )
}

export default UpcomingBillCard
